import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../services/api';
import { EndpointType, ProcessingStepType } from '../../types/index';
import type {
  PipelineDefaultsDto,
  PipelineDefaultsListDto,
  PipelineDefaultsSummaryDto,
  PipelineEdgeDto,
  PipelineGraphDto,
  PipelineNodeDto,
} from '../../types/index';
import type {
  CorrelationConfig,
  EnrichmentConfig,
  IncidentCreationConfig,
  NotificationStepConfig,
} from '../../types/pipelineConfig';
import { PipelineFlowEditor } from '../pipeline';
import {
  getDefaultCorrelationConfig,
  getDefaultDeduplicationConfig,
  getDefaultEnrichmentConfig,
  getDefaultIncidentCreationConfig,
  getDefaultIncidentGateConfig,
  getDefaultNormalizationConfig,
  getDefaultNotificationConfig,
  getDefaultSuppressionConfig,
} from '../../utils/pipelineDefaults';

const STEP_ORDER: ProcessingStepType[] = [
  ProcessingStepType.Validation,
  ProcessingStepType.Normalization,
  ProcessingStepType.Deduplication,
  ProcessingStepType.Suppression,
  ProcessingStepType.Enrichment,
  ProcessingStepType.Correlation,
  ProcessingStepType.IncidentGate,
  ProcessingStepType.IncidentCreation,
  ProcessingStepType.Notification,
];

const STEP_LABELS: Record<ProcessingStepType, string> = {
  Validation: 'Validation',
  Normalization: 'Normalization',
  Deduplication: 'Deduplication',
  Suppression: 'Suppression',
  Enrichment: 'Enrichment',
  Correlation: 'Correlation',
  IncidentGate: 'Incident Gate',
  IncidentCreation: 'Incident Creation',
  Notification: 'Notification',
  Initialization: 'Initialization',
  Custom: 'Custom',
};

function readIsEnabled(config: unknown): boolean {
  if (typeof config !== 'object' || config === null) {
    return true;
  }

  const candidate = config as { isEnabled?: unknown };
  return typeof candidate.isEnabled === 'boolean' ? candidate.isEnabled : true;
}

function parseJsonOrEmpty(value: string | undefined): Record<string, unknown> {
  if (!value) {
    return {};
  }

  try {
    const parsed = JSON.parse(value);
    if (typeof parsed === 'object' && parsed !== null) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    // Fallback to empty object for invalid JSON.
  }

  return {};
}

function generateDefaultGraph(
  configs: Record<string, unknown>,
  endpointType: string,
): PipelineGraphDto {
  const nodes: PipelineNodeDto[] = STEP_ORDER.map((stepType, index) => {
    const config = configs[stepType] ?? {};
    return {
      id: `default-${stepType.toLowerCase()}`,
      nodeType: 'Step',
      stepType,
      name: STEP_LABELS[stepType] || stepType,
      positionX: 0,
      positionY: index * 150,
      isEnabled: readIsEnabled(config),
      configuration: JSON.stringify(config),
      conditionField: undefined,
      conditionOperator: undefined,
    };
  });

  const edges: PipelineEdgeDto[] = nodes.slice(0, -1).map((node, index) => ({
    id: `default-edge-${index}`,
    sourceNodeId: node.id,
    targetNodeId: nodes[index + 1].id,
    label: undefined,
    conditionValue: undefined,
    sortOrder: index,
    isDefault: true,
  }));

  return {
    pipelineConfigurationId: `defaults-${endpointType}`,
    nodes,
    edges,
  };
}

function extractConfigsFromGraph(nodes: PipelineNodeDto[]): Record<string, unknown> {
  const configs: Record<string, unknown> = {};
  for (const node of nodes) {
    if (node.nodeType === 'Step' && node.stepType) {
      configs[node.stepType] = parseJsonOrEmpty(node.configuration);
    }
  }
  return configs;
}

function loadFrontendDefaults(endpointType: string) {
  return {
    [ProcessingStepType.Validation]: { isEnabled: true },
    [ProcessingStepType.Normalization]: getDefaultNormalizationConfig(endpointType),
    [ProcessingStepType.Deduplication]: getDefaultDeduplicationConfig(endpointType),
    [ProcessingStepType.Suppression]: getDefaultSuppressionConfig(endpointType),
    [ProcessingStepType.Enrichment]: getDefaultEnrichmentConfig(endpointType),
    [ProcessingStepType.Correlation]: getDefaultCorrelationConfig(endpointType),
    [ProcessingStepType.IncidentGate]: getDefaultIncidentGateConfig(endpointType),
    [ProcessingStepType.IncidentCreation]: getDefaultIncidentCreationConfig(endpointType),
    [ProcessingStepType.Notification]: getDefaultNotificationConfig(endpointType),
  };
}

function sanitizeIncidentCreationDefaults(config: IncidentCreationConfig): IncidentCreationConfig {
  return {
    ...config,
    autoAssignTeamId: undefined,
  };
}

function sanitizeNotificationDefaults(config: NotificationStepConfig): NotificationStepConfig {
  return {
    ...config,
    targets: (config.targets ?? []).map((target) => ({
      ...target,
      emailAddresses: target.channel === 'email' ? [] : target.emailAddresses,
      webhookUrl:
        target.channel === 'teams' || target.channel === 'slack' || target.channel === 'webhook'
          ? ''
          : target.webhookUrl,
      channelName:
        target.channel === 'teams' || target.channel === 'slack'
          ? ''
          : target.channelName,
      customHeaders:
        target.channel === 'webhook'
          ? {}
          : target.customHeaders,
    })),
  };
}

function sanitizeEnrichmentDefaults(config: EnrichmentConfig): EnrichmentConfig {
  return {
    ...config,
    fallbackBehavior: config.fallbackBehavior
      ? {
          ...config.fallbackBehavior,
          defaultPrimaryCiId: undefined,
        }
      : config.fallbackBehavior,
    ciMatchingRules: (config.ciMatchingRules ?? []).map((rule) => ({
      ...rule,
      ciClassIds: [],
      relatedCiClassIds: [],
    })),
    automationLookups: (config.automationLookups ?? []).map((lookup) => ({
      ...lookup,
      scriptId: undefined,
      serviceConnectionId: null,
    })),
  };
}

function sanitizeCorrelationDefaults(config: CorrelationConfig): CorrelationConfig {
  return {
    ...config,
    correlationRuleIds: [],
    rules: (config.rules ?? []).map((rule) => ({
      ...rule,
      patternId: rule.method === 'pattern' ? '' : rule.patternId,
    })),
  };
}

function buildConfigsForEndpoint(endpointType: string, serverData?: PipelineDefaultsDto): Record<string, unknown> {
  const defaults = loadFrontendDefaults(endpointType);
  const serverDataRecord = serverData as (PipelineDefaultsDto & Record<string, unknown>) | undefined;

  return {
    [ProcessingStepType.Validation]:
      (serverDataRecord?.validation as Record<string, unknown> | undefined)
      ?? (serverDataRecord?.Validation as Record<string, unknown> | undefined)
      ?? (defaults[ProcessingStepType.Validation] as Record<string, unknown>),
    [ProcessingStepType.Normalization]:
      serverData?.normalization
        ? serverData.normalization
        : defaults[ProcessingStepType.Normalization],
    [ProcessingStepType.Deduplication]:
      serverData?.deduplication
        ? serverData.deduplication
        : defaults[ProcessingStepType.Deduplication],
    [ProcessingStepType.Suppression]:
      serverData?.suppression
        ? serverData.suppression
        : defaults[ProcessingStepType.Suppression],
    [ProcessingStepType.Enrichment]:
      serverData?.enrichment
        ? sanitizeEnrichmentDefaults(serverData.enrichment as EnrichmentConfig)
        : sanitizeEnrichmentDefaults(defaults[ProcessingStepType.Enrichment] as EnrichmentConfig),
    [ProcessingStepType.Correlation]:
      serverData?.correlation
        ? sanitizeCorrelationDefaults(serverData.correlation as CorrelationConfig)
        : sanitizeCorrelationDefaults(defaults[ProcessingStepType.Correlation] as CorrelationConfig),
    [ProcessingStepType.IncidentGate]:
      serverData?.incidentGate
        ? serverData.incidentGate
        : defaults[ProcessingStepType.IncidentGate],
    [ProcessingStepType.IncidentCreation]:
      serverData?.incidentCreation
        ? sanitizeIncidentCreationDefaults(serverData.incidentCreation as IncidentCreationConfig)
        : sanitizeIncidentCreationDefaults(
          defaults[ProcessingStepType.IncidentCreation] as IncidentCreationConfig,
        ),
    [ProcessingStepType.Notification]:
      serverData?.notification
        ? sanitizeNotificationDefaults(serverData.notification as NotificationStepConfig)
        : sanitizeNotificationDefaults(defaults[ProcessingStepType.Notification] as NotificationStepConfig),
  };
}

export function PipelineDefaultsTab() {
  const [selectedEndpointType, setSelectedEndpointType] = useState<string>(EndpointType.AzureMonitor);
  const [summaries, setSummaries] = useState<Record<string, PipelineDefaultsSummaryDto>>({});
  const [graph, setGraph] = useState<PipelineGraphDto>(
    () => generateDefaultGraph(loadFrontendDefaults(EndpointType.AzureMonitor), EndpointType.AzureMonitor),
  );
  const [loading, setLoading] = useState(true);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadSummaries = useCallback(async () => {
    try {
      const data: PipelineDefaultsListDto = await apiClient.getAllPipelineDefaults();
      const map: Record<string, PipelineDefaultsSummaryDto> = {};
      for (const summary of data.endpointTypes) {
        map[summary.endpointType] = summary;
      }
      setSummaries(map);
    } catch {
      // Non-critical. Keep existing summary map.
    }
  }, []);

  const applyConfigs = useCallback((endpointType: string, serverData?: PipelineDefaultsDto) => {
    const configs = buildConfigsForEndpoint(endpointType, serverData);
    setGraph(generateDefaultGraph(configs, endpointType));
  }, []);

  const loadEndpointConfig = useCallback(async (endpointType: string) => {
    setLoadingConfig(true);
    setError(null);
    try {
      const hasCustom = summaries[endpointType]?.hasCustomDefaults;
      if (hasCustom) {
        const data = await apiClient.getPipelineDefaultsByEndpoint(endpointType);
        applyConfigs(endpointType, data);
      } else {
        applyConfigs(endpointType);
      }
    } catch {
      applyConfigs(endpointType);
    } finally {
      setLoadingConfig(false);
    }
  }, [applyConfigs, summaries]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadSummaries();
      setLoading(false);
    })();
  }, [loadSummaries]);

  useEffect(() => {
    if (!loading) {
      // Fetching endpoint defaults after summary loading is an intentional side effect.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      void loadEndpointConfig(selectedEndpointType);
    }
  }, [selectedEndpointType, loading, loadEndpointConfig]);

  const handleSaveGraph = useCallback(async (nodes: PipelineNodeDto[], edges: PipelineEdgeDto[]) => {
    setGraph((current) => ({
      pipelineConfigurationId: current.pipelineConfigurationId,
      nodes,
      edges,
    }));

    setSaving(true);
    setSaveMessage(null);

    try {
      const extracted = extractConfigsFromGraph(nodes);

      const payload: Partial<PipelineDefaultsDto> & Record<string, unknown> = {
        endpointType: selectedEndpointType,
        normalization: extracted[ProcessingStepType.Normalization],
        deduplication: extracted[ProcessingStepType.Deduplication],
        suppression: extracted[ProcessingStepType.Suppression],
        enrichment: extracted[ProcessingStepType.Enrichment],
        correlation: extracted[ProcessingStepType.Correlation],
        incidentGate: extracted[ProcessingStepType.IncidentGate],
        incidentCreation: extracted[ProcessingStepType.IncidentCreation],
        notification: extracted[ProcessingStepType.Notification],
      };

      if (extracted[ProcessingStepType.Validation] !== undefined) {
        payload.validation = extracted[ProcessingStepType.Validation];
      }

      await apiClient.updatePipelineDefaultsByEndpoint(selectedEndpointType, payload);
      setSaveMessage('Defaults saved successfully');
      await loadSummaries();
    } catch {
      setSaveMessage('Error saving defaults');
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMessage(null), 4000);
    }
  }, [loadSummaries, selectedEndpointType]);

  const handleSave = async () => {
    await handleSaveGraph(graph.nodes, graph.edges);
  };

  const handleReset = async () => {
    setSaving(true);
    setSaveMessage(null);

    try {
      await apiClient.deletePipelineDefaults(selectedEndpointType);
      applyConfigs(selectedEndpointType);
      setSaveMessage('Reset to system defaults');
      await loadSummaries();
    } catch {
      setSaveMessage('Error resetting defaults');
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMessage(null), 4000);
    }
  };

  const selectEndpointType = (endpointType: string) => {
    setSelectedEndpointType(endpointType);
    setSaveMessage(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Loading pipeline defaults…
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-[calc(100vh-16rem)] flex-col">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Pipeline Step Defaults
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Configure default pipeline step settings for each endpoint type. These
          defaults are used when creating new pipeline configurations or when
          resetting an existing pipeline to defaults.
        </p>
      </div>

      {/* Endpoint Type Selector */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Endpoint Type
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
          {Object.values(EndpointType).map((endpointType) => (
            <button
              key={endpointType}
              onClick={() => selectEndpointType(endpointType)}
              className={`p-3 rounded-lg border-2 text-center text-sm font-medium transition-all ${
                selectedEndpointType === endpointType
                  ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-900/20 dark:text-blue-300'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 text-gray-700 dark:text-gray-300'
              }`}
            >
              <span>{endpointType}</span>
              {summaries[endpointType]?.hasCustomDefaults && (
                <span className="block mt-1 text-xs text-green-600 dark:text-green-400">
                  ● Custom
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {loadingConfig ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3" />
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Loading configuration…
          </span>
        </div>
      ) : (
        <>
          <div className="h-[calc(100vh-27rem)] min-h-[600px] w-full border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <PipelineFlowEditor
              graph={graph}
              onSave={handleSaveGraph}
              endpointType={selectedEndpointType}
            />
          </div>

          <div className="mt-4 flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="rounded-md bg-primary-700 px-4 py-2 text-sm font-medium text-white hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Defaults'}
            </button>
            <button
              onClick={handleReset}
              disabled={saving}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Reset to System Defaults
            </button>
            {saveMessage && (
              <span
                className={`text-sm ${saveMessage.includes('Error') ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}
              >
                {saveMessage}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
